/**
 * Promo Registration page — ported from dashboard/app/register/promo/page.tsx.
 * Uses TanStack Router APIs instead of next/navigation.
 */
import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { registerWithPromoCode } from '../lib/auth-client'
import { RegisterHeader } from '../components/RegisterHeader'
import { Loader2, CheckCircle2, Ticket } from 'lucide-react'

export const Route = createFileRoute('/register-promo')({
  head: () => ({
    meta: [{ title: 'Promo Registration | Unbreakable' }],
  }),
  component: PromoRegisterPage,
})

function PromoRegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await registerWithPromoCode(email.trim(), name.trim(), promoCode.trim().toUpperCase())
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-linear-to-b from-[#f7f6fb] to-[#efecf7] flex flex-col">
        <RegisterHeader />
        <main className="flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-12">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[#ddd5ea] p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Check your email</h1>
            <p className="text-gray-600 mb-6">
              We sent a link to <strong>{email}</strong>. Click it to complete your account--you can set a password or sign in with Google.
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-hover transition-colors"
            >
              Go to Sign in
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-[#f7f6fb] to-[#efecf7] flex flex-col">
      <RegisterHeader />
      <main className="flex-1 flex flex-col items-center justify-start px-6 md:px-10 pt-24 md:pt-28 pb-10 md:pb-14">
        <div className="w-full max-w-md mb-6 text-center">
          <h1 className="text-[28px] md:text-[34px] leading-tight font-semibold text-[#2e2445]">
            Redeem a promo code
          </h1>
          <p className="mt-1 text-sm md:text-base text-[#5e5770]">
            Enter your details and code to create your account. We&apos;ll email you a link to finish signing up.
          </p>
        </div>

        {error && (
          <div className="w-full max-w-md mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="w-full max-w-md rounded-2xl border border-[#ddd5ea] bg-white/55 shadow-[0_20px_50px_rgba(48,34,78,0.08)] p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setError('')
                }}
                placeholder="Your name"
                autoComplete="name"
                required
                disabled={loading}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError('')
                }}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={loading}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                Promo code
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Ticket className="w-5 h-5" />
                </div>
                <input
                  id="promoCode"
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase())
                    setError('')
                  }}
                  placeholder="XXXX-XXXX-XXXX"
                  autoComplete="off"
                  required
                  disabled={loading}
                  className="w-full pl-10 px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all disabled:bg-gray-50"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-brand text-white font-medium rounded-xl hover:bg-brand-hover active:bg-brand-active disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>

          <p className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            After you submit, we&apos;ll send you an email. Click the link to set a password or sign in with Google.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          <Link to="/register" className="text-brand font-medium hover:underline">
            &larr; Back to subscription signup
          </Link>
        </p>
      </main>
    </div>
  )
}
